import { DataExplorerTable } from 'features/data-explorer/types'
import NewExportActions from './NewExportActions'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useExportHooks from '../../useExportHooks'
import { buildQueryForSubscriptionExport } from 'features/common/queries/tableQueries'
import { useSubscriptionDatesQuery } from 'features/data-explorer/hooks/dataExplorerHooks'
import NewExportForm from './NewExportForm'
import NewExportSqlAlert from './NewExportSqlAlert'
import Loader from 'features/common/ui/utils/Loader'
import { Box } from '@mui/material'
import { CodeAreaDialogContent, CodeAreaField } from '@carto/meridian-ds/components'

interface NewExportProps {
  table: DataExplorerTable
}

function NewExport({ table }: NewExportProps) {
  const { uri, fqn, geoFqn, handleExport } = useExportHooks(table)
  const { data: dates, isLoading: datesLoading } = useSubscriptionDatesQuery(fqn)

  const [query, setQuery] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [isSqlEditorVisible, setSqlEditorVisible] = useState<boolean>(false)
  const [sqlEditorWarningVisible, setSqlEditorWarningVisible] = useState<boolean>(false)
  const [queryHasChanged, setQueryHasChanged] = useState<boolean>(false)
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const isFormVisible = !isSqlEditorVisible

  const datesOptions = useMemo(
    () =>
      dates?.map((date) => ({
        value: date.do_date,
        label: date.do_date
      })),
    [dates]
  )

  const disabled = useMemo(() => {
    return Boolean(isFormVisible && datesOptions?.length && selectedDates.length === 0)
  }, [isFormVisible, datesOptions, selectedDates])

  const resetQuery = useCallback(() => {
    if (!uri) return

    const query = buildQueryForSubscriptionExport({
      uri,
      fqn,
      geoFqn,
      datesFilters: selectedDates
    })
    setQueryHasChanged(false)
    setQuery(query)
  }, [fqn, geoFqn, selectedDates, uri])

  const confirmSqlEditorVisibleChange = useCallback(() => {
    resetQuery()
    setSqlEditorWarningVisible(false)
    setSqlEditorVisible(false)
  }, [resetQuery])

  const handleQueryChange = (query) => {
    setQueryHasChanged(true)
    setQuery(query)
  }

  const handleSqlEditorVisibleChange = useCallback(
    (visible) => {
      if (!visible && queryHasChanged) {
        setSqlEditorWarningVisible(true)
      } else {
        setSqlEditorVisible(visible)
      }
    },
    [queryHasChanged]
  )

  const onSubmit = () => {
    handleExport(query)
    setSubmitting(true)
  }

  useEffect(() => {
    resetQuery()
  }, [resetQuery])

  useEffect(() => {
    if (!datesLoading && datesOptions) {
      setSelectedDates(datesOptions.map((d) => d.value))
    }
  }, [datesLoading, datesOptions])

  if (datesLoading) {
    return (
      <CodeAreaDialogContent withBorder={false}>
        <Box py={14}>
          <Loader />
        </Box>
      </CodeAreaDialogContent>
    )
  }

  return (
    <>
      <CodeAreaDialogContent withGutter={!isSqlEditorVisible} withBorder={isSqlEditorVisible}>
        {isSqlEditorVisible ? (
          <CodeAreaField
            value={query}
            copyValue={query}
            onChange={(value) => handleQueryChange(value)}
            variant={null}
          />
        ) : (
          <NewExportForm
            dates={datesOptions}
            selectedDates={selectedDates}
            table={table}
            onSelectDates={setSelectedDates}
          />
        )}
      </CodeAreaDialogContent>
      <NewExportActions
        disabled={disabled}
        submitting={submitting}
        onSubmit={onSubmit}
        sqlEditor={isSqlEditorVisible}
        onSqlEditorVisibleChange={handleSqlEditorVisibleChange}
      />

      {sqlEditorWarningVisible && (
        <NewExportSqlAlert
          opened={sqlEditorWarningVisible}
          onCancel={() => setSqlEditorWarningVisible(false)}
          onConfirm={() => confirmSqlEditorVisibleChange()}
        />
      )}
    </>
  )
}

export default NewExport
